export type TerrainId = "grass" | "stone" | "wood" | "ice" | "water" | "path";

export interface SpawnPoint {
  x: number;
  y: number;
  index: number;
}

export interface Portal {
  x: number;
  y: number;
  targetZoneId: string;
  targetSpawnIndex: number;
}

export interface TilemapJson {
  zoneId: string;
  width: number;
  height: number;
  tileWidth: number;
  tileHeight: number;
  isSafe: boolean;
  background: TerrainId[];
  collision: boolean[];
  spawnPoints: SpawnPoint[];
  portals: Portal[];
}

export function tileIndex(map: { width: number }, x: number, y: number): number {
  return y * map.width + x;
}

export function inBounds(map: { width: number; height: number }, x: number, y: number): boolean {
  return x >= 0 && y >= 0 && x < map.width && y < map.height;
}

export function isWalkable(map: TilemapJson, x: number, y: number): boolean {
  if (!inBounds(map, x, y)) return false;
  return !map.collision[tileIndex(map, x, y)];
}

// =============================================================================
// M11 — chunked terrain streaming
// =============================================================================
//
// At zone-enter, the server sends only metadata (dims, isSafe, spawnPoints,
// portals — see protocol.ts ZoneEnter). The actual terrain + collision
// arrives in CHUNK_SIZE × CHUNK_SIZE blocks via `chunkData` messages,
// pushed by the server based on which chunks intersect the player's
// viewport (see server/src/sim/world.ts viewport tracking).
//
// CHUNK_SIZE = 32 — sweet spot between message overhead (smaller chunks
// = more messages) and granularity (larger chunks = wasted bandwidth on
// tiles you can't see). A 1024² zone has 1024 chunks; only the ~25
// around a player are ever sent.
//
// Chunks are keyed by their grid coords `${cx},${cy}` where
// (cx, cy) = (floor(x/32), floor(y/32)). The chunk at (cx, cy) covers
// world tiles from (cx*32, cy*32) inclusive to ((cx+1)*32, (cy+1)*32)
// exclusive. Edge chunks may be partially out-of-bounds — the server
// trims to actual zone dimensions before sending.

export const CHUNK_SIZE = 32;

export type ChunkKey = string; // "cx,cy"

export interface ChunkCoord {
  cx: number;
  cy: number;
}

export function chunkKey(cx: number, cy: number): ChunkKey {
  return `${cx},${cy}`;
}

export function parseChunkKey(key: ChunkKey): ChunkCoord {
  const [cxStr, cyStr] = key.split(",");
  return { cx: Number.parseInt(cxStr ?? "0", 10), cy: Number.parseInt(cyStr ?? "0", 10) };
}

// World tile (x,y) → owning chunk coord.
export function chunkAt(x: number, y: number): ChunkCoord {
  return { cx: Math.floor(x / CHUNK_SIZE), cy: Math.floor(y / CHUNK_SIZE) };
}

// Iterate every chunk that overlaps a (width × height) zone. Edge chunks
// may extend past zone bounds; consumers must clip with inBounds when
// extracting tile data.
export function* iterChunks(zoneWidth: number, zoneHeight: number): Generator<ChunkCoord> {
  const cxMax = Math.ceil(zoneWidth / CHUNK_SIZE);
  const cyMax = Math.ceil(zoneHeight / CHUNK_SIZE);
  for (let cy = 0; cy < cyMax; cy++) {
    for (let cx = 0; cx < cxMax; cx++) {
      yield { cx, cy };
    }
  }
}

// Compute the set of chunks visible from a center tile within `radius`
// chunks (inclusive). e.g. radius=2 around chunk (5,5) yields a 5×5
// area: cx in [3..7], cy in [3..7]. Out-of-bounds chunks are filtered
// against the zone dimensions.
export function viewportChunks(
  centerTileX: number,
  centerTileY: number,
  zoneWidth: number,
  zoneHeight: number,
  radius: number,
): ChunkKey[] {
  const center = chunkAt(centerTileX, centerTileY);
  const cxMax = Math.ceil(zoneWidth / CHUNK_SIZE);
  const cyMax = Math.ceil(zoneHeight / CHUNK_SIZE);
  const out: ChunkKey[] = [];
  for (let dy = -radius; dy <= radius; dy++) {
    for (let dx = -radius; dx <= radius; dx++) {
      const cx = center.cx + dx;
      const cy = center.cy + dy;
      if (cx < 0 || cy < 0 || cx >= cxMax || cy >= cyMax) continue;
      out.push(chunkKey(cx, cy));
    }
  }
  return out;
}

// Slice a chunk worth of terrain + collision out of a flat zone array.
// Chunks at the right/bottom edges may be smaller than CHUNK_SIZE on one
// or both axes (when zone dims aren't multiples of CHUNK_SIZE) — the
// returned arrays carry the actual extracted region size, which can be
// less than CHUNK_SIZE * CHUNK_SIZE.
//
// Reused as the on-wire ChunkData shape (see protocol.ts) and the
// client-side per-chunk storage (see client/src/render/tilemap.ts).
export interface ChunkData {
  cx: number;
  cy: number;
  width: number;  // ≤ CHUNK_SIZE
  height: number; // ≤ CHUNK_SIZE
  terrain: TerrainId[];
  collision: boolean[];
  // M14 — decor placements that fall within this chunk's tile bounds.
  // Empty when the zone has no decor. Imported lazily via the type below
  // to avoid a circular import (data/zones.ts → world/tilemap.ts already
  // exists for TerrainId).
  decor: { kind: string; tx: number; ty: number }[];
}

export function extractChunk(
  zoneWidth: number,
  zoneHeight: number,
  zoneTerrain: readonly TerrainId[],
  zoneCollision: readonly boolean[],
  cx: number,
  cy: number,
  zoneDecor: readonly { kind: string; tx: number; ty: number }[] = [],
): ChunkData {
  const x0 = cx * CHUNK_SIZE;
  const y0 = cy * CHUNK_SIZE;
  const x1 = Math.min(x0 + CHUNK_SIZE, zoneWidth);
  const y1 = Math.min(y0 + CHUNK_SIZE, zoneHeight);
  const w = x1 - x0;
  const h = y1 - y0;
  const terrain: TerrainId[] = new Array(w * h);
  const collision: boolean[] = new Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const zoneIx = (y0 + y) * zoneWidth + (x0 + x);
      terrain[y * w + x] = zoneTerrain[zoneIx]!;
      collision[y * w + x] = zoneCollision[zoneIx]!;
    }
  }
  // Filter zone decor to placements that fall inside this chunk's tile
  // bounds. Linear scan is fine here — even a 1024² zone with thousands
  // of decor entries is single-digit ms per chunk extract, and chunks
  // are only built on viewport crossings (handful per second worst case).
  const decor: { kind: string; tx: number; ty: number }[] = [];
  for (const d of zoneDecor) {
    if (d.tx >= x0 && d.tx < x1 && d.ty >= y0 && d.ty < y1) decor.push(d);
  }
  return { cx, cy, width: w, height: h, terrain, collision, decor };
}
